import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import LoadingSplash from "./components/LoadingSplash";
import Layout from "./components/Layout";
import PageTransition from "./components/PageTransition";

// Handle stale chunk errors from code splitting — auto-refresh once
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('Loading chunk')) {
    const reloaded = sessionStorage.getItem('chunk_reload');
    if (!reloaded) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
    }
  }
});
// Clear reload flag on successful load
sessionStorage.removeItem('chunk_reload');
import { SiteAuthProvider, ProtectedRoute, useSiteAuth } from "./components/SiteAuth";
import { VanillaBlocksProvider } from "./context/VanillaBlocksContext";
import { HighResTexturesProvider } from "./context/HighResTexturesContext";
import { CartProvider } from "./context/CartContext";
import { ShopAuthProvider } from "./context/ShopAuthContext";
import ShopHeaderCluster from "./components/marketplace/ShopHeaderCluster";
import SignInModal from "./components/marketplace/SignInModal";
import { Toaster } from "./components/ui/sonner";
import NewLandingPage from "./pages/NewLandingPage";
import MaintenancePage from "./pages/MaintenancePage";
import { preloadAllBanners, preloadCriticalImages } from "./utils/preloadImages";
import "./App.css";

// Lazy-loaded pages — only downloaded when the user navigates to them
const BlockBuilder = lazy(() => import("./pages/BlockBuilder"));
const CTMSlicer = lazy(() => import("./pages/CTMSlicer"));
const AssetTools = lazy(() => import("./pages/Compress"));
const TopoImport = lazy(() => import("./pages/TopoImport"));
const WorldGenerator = lazy(() => import("./pages/WorldGenerator"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminAuth = lazy(() => import("./components/AdminAuth"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Login = lazy(() => import("./pages/Login"));
const AuthSuccess = lazy(() => import("./pages/AuthSuccess"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const HelpCenter = lazy(() => import("./pages/HelpCenter").then(m => ({ default: m.HelpCenterHome })));
const HelpArticle = lazy(() => import("./pages/HelpCenter").then(m => ({ default: m.HelpArticlePage })));
const FluxAssistant = lazy(() => import("./components/FluxAssistant"));

// Studio-app routes where Flux should NOT appear (per user directive: keep Flux to the
// marketing / website side only). Anything at /studio, plus the legacy standalone routes
// that mount PixelSculpt / RenderStudio / VanillaStudio / ConnectedTextures / ResourcePack
// / AssetStudio outside the /studio shell.
const STUDIO_APP_ROUTES = [
  "/studio",
  "/block-builder",
  "/render-studio",
  "/vanilla-studio",
  "/connected-textures",
  "/resource-pack",
  "/asset-tools",
  "/world-builder",
  "/pixelsculpt",
  "/arcade",
];
function GlobalFluxAssistantGate() {
  const location = useLocation();
  const p = (location.pathname || "").toLowerCase();
  const isStudio = STUDIO_APP_ROUTES.some(r => p === r || p.startsWith(r + "/"));
  if (isStudio) return null;
  return <FluxAssistant />;
}
const Contact = lazy(() => import("./pages/legal/Contact"));
const About = lazy(() => import("./pages/legal/About"));
const Roadmap = lazy(() => import("./pages/legal/Roadmap"));
const LatestNews = lazy(() => import("./pages/legal/LatestNews"));
const Projects = lazy(() => import("./pages/legal/Projects"));
const BlockRendererTest = lazy(() => import("./pages/BlockRendererTest"));
const SchematicTest = lazy(() => import("./pages/SchematicTest"));
// ProductDemo archived — see /app/archive/ProductDemo/
const ResourcePackBuilder = lazy(() => import("./pages/ResourcePackBuilder"));
const ProductOverviewPage = lazy(() => import("./pages/products/ProductOverviewPage"));
const MarketplacePage = lazy(() => import("./pages/products/MarketplacePage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentSuccessPage = lazy(() => import("./pages/payment/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("./pages/payment/PaymentCancelPage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const ShopAuthCallbackPage = lazy(() => import("./pages/auth/ShopAuthCallbackPage"));
const TextureLibraryPage = lazy(() => import("./pages/products/TextureLibraryPage"));
const TemplateDemo = lazy(() => import("./pages/TemplateDemo"));
const StudioApp = lazy(() => import("./pages/StudioApp"));
const SkinEditor = lazy(() => import("./pages/SkinEditor"));
const ColorPaletteGenerator = lazy(() => import("./pages/ColorPaletteGenerator"));
const FindAPlayer = lazy(() => import("./pages/FindAPlayer"));
const NotFound = lazy(() => import("./pages/legal/NotFound"));

// Critical landing page images load immediately
preloadCriticalImages();

// Defer remaining banner preloading until after initial paint
if (typeof window !== 'undefined') {
  const startPreload = () => preloadAllBanners();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(startPreload, { timeout: 3000 });
  } else {
    setTimeout(startPreload, 2000);
  }
}

// Minimal suspense fallback — just a black screen matching the app background
const SuspenseFallback = () => (
  <div style={{ position: "fixed", inset: 0, background: "#0a0a0a", zIndex: 1 }} />
);

/**
 * Persistent splash shown after admin login across the full page reload.
 * Waits for SiteAuth to finish verifying (min 1.5s, max 8s) then fades out.
 */
function StudioEntrySplash({ onDone }) {
  const { isLoading } = useSiteAuth();
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinTimeElapsed(true), 3000);
    return () => clearTimeout(t);
  }, []);

  // Safety: force-dismiss after 8s no matter what
  useEffect(() => {
    const t = setTimeout(() => {
      setFading(true);
      setTimeout(onDone, 600);
    }, 8000);
    return () => clearTimeout(t);
  }, [onDone]);

  // Normal path: fade once auth is done AND min time elapsed
  useEffect(() => {
    if (minTimeElapsed && !isLoading && !fading) {
      setFading(true);
      const t = setTimeout(onDone, 600);
      return () => clearTimeout(t);
    }
  }, [minTimeElapsed, isLoading, fading, onDone]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      background: "#000",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      opacity: fading ? 0 : 1,
      transition: "opacity 0.6s ease",
      pointerEvents: fading ? "none" : "all",
    }}>
      <img src="/lumenforge-f-logo.webp" alt="LumenForge" style={{ width: 64, height: 64, marginBottom: 20, objectFit: "contain" }} />
      <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: "0.04em" }}>LumenForge</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", marginTop: 8, letterSpacing: "0.12em", textTransform: "uppercase" }}>Build · Cut · Ship</div>
      <div style={{ position: "absolute", bottom: 40, fontSize: 11, color: "rgba(255,255,255,0.5)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
        Part of the Inside the Cube ecosystem
      </div>
    </div>
  );
}

function App() {
  // Site-wide maintenance lock. Every URL serves the holding page until the
  // admin PIN is entered; the unlock lives in sessionStorage so it clears when
  // the tab closes.
  const [maintenanceUnlocked, setMaintenanceUnlocked] = useState(
    () => sessionStorage.getItem("lf_maintenance_unlocked") === "true"
  );
  const unlockMaintenance = useCallback(() => {
    setMaintenanceUnlocked(true);
    window.history.replaceState(null, "", "/");
  }, []);

  // Welcome splash removed — the landing page loads directly. The
  // homepage's own hero video handles the intro visual, so there's no
  // need for a black LumenForge card before it. Studio and admin
  // routes still get their own persistent splashes where they help
  // (auth flow, admin login) but every public marketing / product /
  // legal page now goes straight to content.

  // Detect admin login transition — show persistent splash until auth is confirmed
  const [showStudioSplash, setShowStudioSplash] = useState(() => {
    if (sessionStorage.getItem("lf_studio_entering")) {
      sessionStorage.removeItem("lf_studio_entering");
      return true;
    }
    return false;
  });

  const [showAdminSplash, setShowAdminSplash] = useState(() => {
    if (sessionStorage.getItem("lf_admin_entering")) {
      sessionStorage.removeItem("lf_admin_entering");
      return true;
    }
    return false;
  });

  // Hide the platform badge
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) {
      badge.style.setProperty('display', 'none', 'important');
      badge.style.setProperty('visibility', 'hidden', 'important');
      badge.style.setProperty('opacity', '0', 'important');
      badge.style.setProperty('pointer-events', 'none', 'important');
    }
    const style = document.createElement('style');
    style.textContent = '#emergent-badge { display: none !important; visibility: hidden !important; opacity: 0 !important; }';
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  if (!maintenanceUnlocked) {
    return (
      <div className="App dark">
        <MaintenancePage onUnlock={unlockMaintenance} />
      </div>
    );
  }

  return (
    <div className="App dark">
      {/* Admin splash during redirect */}
      {showAdminSplash && (
        <LoadingSplash
          onComplete={() => setShowAdminSplash(false)}
          holdTime={2000}
          fadeDuration={800}
          subtitle="Loading Admin Panel"
        />
      )}
      {/* Custom background with frost effect - uses public folder image */}
      <div 
        className="app-background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/assets/app_background.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.12,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <div 
        className="app-frost-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10, 10, 10, 0.85)',
          backdropFilter: 'blur(2px)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />
      <Toaster position="bottom-center" />
      <VanillaBlocksProvider>
        <HighResTexturesProvider>
          <CartProvider>
            <BrowserRouter>
            <ShopAuthProvider>
            <SiteAuthProvider>
              <ShopHeaderCluster />
              <SignInModal />
              {showStudioSplash && <StudioEntrySplash onDone={() => setShowStudioSplash(false)} />}
              <Routes>
                {/* Product Demo/Brief - PUBLIC, FULL SCREEN, no layout/authentication */}
                {/* Product brief archived — route disabled */}
                
                {/* Landing page - NEW premium design */}
                <Route path="/" element={<NewLandingPage />} />
                
                {/* Original landing page - preserved */}
                <Route path="/landing-classic" element={<Suspense fallback={<SuspenseFallback />}><LandingPage /></Suspense>} />
                
                {/* Legal/Info pages - PUBLIC, own layout */}
                <Route path="/privacy" element={<Suspense fallback={<SuspenseFallback />}><PrivacyPolicy /></Suspense>} />
                <Route path="/terms" element={<Suspense fallback={<SuspenseFallback />}><Terms /></Suspense>} />
                <Route path="/refund-policy" element={<Suspense fallback={<SuspenseFallback />}><RefundPolicy /></Suspense>} />
                <Route path="/cookie-policy" element={<Suspense fallback={<SuspenseFallback />}><CookiePolicy /></Suspense>} />
                <Route path="/cookies" element={<Navigate to="/cookie-policy" replace />} />
                {/* /legal route retired: Legal Notice content is folded into
                    Terms & Conditions. Redirect any lingering links. */}
                <Route path="/legal" element={<Navigate to="/terms" replace />} />
                <Route path="/faq" element={<Navigate to="/help-center" replace />} />
                <Route path="/help-center" element={<Suspense fallback={<SuspenseFallback />}><HelpCenter /></Suspense>} />
                <Route path="/help-center/:slug" element={<Suspense fallback={<SuspenseFallback />}><HelpArticle /></Suspense>} />
                <Route path="/contact" element={<Suspense fallback={<SuspenseFallback />}><Contact /></Suspense>} />
                <Route path="/about" element={<Suspense fallback={<SuspenseFallback />}><About /></Suspense>} />
                <Route path="/development-hub" element={<Suspense fallback={<SuspenseFallback />}><Roadmap /></Suspense>} />
                {/* /gallery route retired in favour of /products/texture-library */}
                <Route path="/gallery" element={<Navigate to="/products/texture-library" replace />} />
                <Route path="/news" element={<Suspense fallback={<SuspenseFallback />}><LatestNews /></Suspense>} />
                {/* Legacy /news/:slug internal reader retired — cards now link directly to Patreon */}
                <Route path="/news/:slug" element={<Navigate to="/news" replace />} />
                <Route path="/projects" element={<Suspense fallback={<SuspenseFallback />}><Projects /></Suspense>} />
                <Route path="/portfolio" element={<Navigate to="/products/marketplace" replace />} />
                
                {/* Template demo page — PUBLIC */}
                <Route path="/template" element={<Suspense fallback={<SuspenseFallback />}><TemplateDemo /></Suspense>} />

                {/* 404 catch-all — full-screen, standalone (no legacy Layout wrapper) */}
                <Route path="/404" element={<Suspense fallback={<SuspenseFallback />}><NotFound /></Suspense>} />
                <Route path="/not-found" element={<Suspense fallback={<SuspenseFallback />}><NotFound /></Suspense>} />
                
                {/* NEW STUDIO APP — Full screen, PUBLIC (auth bypassed per rebrand) */}
                <Route path="/studio" element={<Suspense fallback={<SuspenseFallback />}><StudioApp /></Suspense>} />

                {/* Skin Editor MVP — full-screen pixel editor with live 3D preview.
                    Lives OUTSIDE /studio/* so the global ShopHeaderCluster (cart,
                    profile) renders and the studio dark frost backdrop stays off. */}
                <Route path="/skin-editor" element={<Suspense fallback={<SuspenseFallback />}><SkinEditor /></Suspense>} />
                {/* Backwards-compat redirect for the earlier /studio/skin-editor URL. */}
                <Route path="/studio/skin-editor" element={<Navigate to="/skin-editor" replace />} />

                {/* Colour Palette Generator — standalone tool, own shell like Skin Editor. */}
                <Route path="/palette-generator" element={<Suspense fallback={<SuspenseFallback />}><ColorPaletteGenerator /></Suspense>} />

                {/* LumenForge Arcade — Full screen, PIN-gated (0154) in dev */}
                <Route path="/arcade/find-a-player" element={<Suspense fallback={<SuspenseFallback />}><FindAPlayer /></Suspense>} />
                <Route path="/arcade" element={<Navigate to="/arcade/find-a-player" replace />} />
                
                {/* Features overview page — canonical /features, with
                    /products/overview kept as a redirect for old links. */}
                <Route path="/features" element={<Suspense fallback={<SuspenseFallback />}><ProductOverviewPage /></Suspense>} />
                <Route path="/products/overview" element={<Navigate to="/features" replace />} />
                <Route path="/products/marketplace" element={<Suspense fallback={<SuspenseFallback />}><MarketplacePage /></Suspense>} />
                <Route path="/checkout" element={<Suspense fallback={<SuspenseFallback />}><CheckoutPage /></Suspense>} />
                <Route path="/payment/success" element={<Suspense fallback={<SuspenseFallback />}><PaymentSuccessPage /></Suspense>} />
                <Route path="/payment/cancel" element={<Suspense fallback={<SuspenseFallback />}><PaymentCancelPage /></Suspense>} />
                <Route path="/library" element={<Suspense fallback={<SuspenseFallback />}><LibraryPage /></Suspense>} />
                <Route path="/admin/products" element={<Navigate to="/admin" replace />} />
                <Route path="/auth/shop-callback" element={<Suspense fallback={<SuspenseFallback />}><ShopAuthCallbackPage /></Suspense>} />
                <Route path="/products/block-builder" element={<Navigate to="/features" replace />} />
                <Route path="/products/ctm-slicer" element={<Navigate to="/features" replace />} />
                <Route path="/products/world-builder" element={<Navigate to="/features" replace />} />
                <Route path="/products/asset-tools" element={<Navigate to="/features" replace />} />
                <Route path="/products/base-pack" element={<Navigate to="/products/marketplace" replace />} />
                <Route path="/products/connected-textures" element={<Navigate to="/features" replace />} />
                <Route path="/products/texture-library" element={<Suspense fallback={<SuspenseFallback />}><TextureLibraryPage /></Suspense>} />
                <Route path="/products/resource-pack-builder" element={<Navigate to="/features" replace />} />
                
                {/* Login route removed — auth-success left in place for external redirect flows */}
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/auth-success" element={<Suspense fallback={<SuspenseFallback />}><AuthSuccess /></Suspense>} />
                
                {/* All other routes with Layout */}
                <Route path="/*" element={
                  <Layout>
                    <PageTransition>
                      <Suspense fallback={<SuspenseFallback />}>
                      <Routes>
                      
                        {/* All other routes are password protected */}
                        <Route path="/block-builder" element={
                          <ProtectedRoute><BlockBuilder /></ProtectedRoute>
                        } />
                        <Route path="/ctm-slicer" element={
                          <ProtectedRoute><CTMSlicer /></ProtectedRoute>
                        } />
                        <Route path="/world-builder" element={
                          <ProtectedRoute><TopoImport /></ProtectedRoute>
                        } />
                        {/* Keep old route for backwards compatibility */}
                        <Route path="/topo-import" element={
                          <ProtectedRoute><TopoImport /></ProtectedRoute>
                        } />
                        <Route path="/asset-tools" element={
                          <ProtectedRoute><AssetTools /></ProtectedRoute>
                        } />
                        
                        {/* Resource Pack Builder */}
                        <Route path="/resource-pack" element={
                          <ProtectedRoute><ResourcePackBuilder /></ProtectedRoute>
                        } />
                        
                        {/* Admin — site-wide ProtectedRoute removed so the
                            Studio "Admin" PIN flow (which sets its own
                            `lumenforge_admin_session`) can reach the CMS.
                            AdminAuth still gates the CMS itself for direct
                            /admin URL hits, so security is unchanged. */}
                        <Route path="/admin" element={
                          <AdminAuth><Admin /></AdminAuth>
                        } />
                        
                        {/* World Generator */}
                        <Route path="/world-generator" element={
                          <ProtectedRoute>
                            <WorldGenerator />
                          </ProtectedRoute>
                        } />
                        
                        {/* Block Renderer Test - for debugging */}
                        <Route path="/block-test" element={
                          <BlockRendererTest />
                        } />
                        
                        {/* Schematic Renderer Test - full schematic proof of concept */}
                        <Route path="/schematic-test" element={
                          <SchematicTest />
                        } />
                        
                        {/* 404 catch-all for unknown routes — uses standalone NotFound page (no Layout wrapper) */}
                        <Route path="*" element={<Suspense fallback={<SuspenseFallback />}><NotFound /></Suspense>} />
                      </Routes>
                      </Suspense>
                    </PageTransition>
                  </Layout>
                } />
              </Routes>
              {/* Flux virtual assistant — floats bottom-right on the marketing/site pages only.
                  Hidden inside the Studio app (/studio and every legacy studio route). */}
              <Suspense fallback={null}>
                <GlobalFluxAssistantGate />
              </Suspense>
            </SiteAuthProvider>
            </ShopAuthProvider>
          </BrowserRouter>
          </CartProvider>
        </HighResTexturesProvider>
      </VanillaBlocksProvider>
    </div>
  );
}

export default App;
